/**
 * Каталог столбцов списка «Карточки SEO» и настройка рабочего места над ним:
 * порядок и видимость. Отдельным модулем, потому что каталог растёт с каждой
 * задачей — столбцы показателей уже дописываются из `metrics.ts`, — и вместе
 * с остальной логикой страницы он перерос разумный размер файла.
 *
 * Порядок и видимость — не ссылка, а настройка рабочего места: контент-менеджер
 * и владелец смотрят на разные столбцы, и переслать друг другу свой набор им
 * незачем. Поэтому хранилище, а не адрес.
 */

import { METRIC_COLUMNS } from './metrics'
import type { SeoCardsColumnDef, SeoCardsColumnKey } from './types'

/**
 * Определения всех столбцов в порядке по умолчанию. Столбцы показателей
 * дописываются из `metrics.ts` в конец: они той же природы (настройка
 * рабочего места), но стоят запроса к аналитике и потому скрыты.
 */
export const SEO_CARDS_COLUMNS: readonly SeoCardsColumnDef[] = [
  { key: 'article', label: 'Товар и артикул', locked: true },
  { key: 'category', label: 'Категория' },
  { key: 'state', label: 'Состояние' },
  { key: 'issues', label: 'Проверки' },
  { key: 'textAge', label: 'Текст менялся' },
  { key: 'seller', label: 'Кабинет', align: 'right' },
  { key: 'action', label: 'Действие', locked: true, align: 'right' },
  { key: 'externalId', label: 'ID карточки' },
  { key: 'theme', label: 'Тематика' },
  { key: 'categoryCode', label: 'Код категории' },
  { key: 'sync', label: 'Синхронизация' },
  { key: 'critical', label: 'Критичных', align: 'right' },
  { key: 'warning', label: 'Предупреждений', align: 'right' },
  ...METRIC_COLUMNS,
]

/** Столбцы, видимые по умолчанию. */
export const DEFAULT_VISIBLE_COLUMNS: readonly SeoCardsColumnKey[] = [
  'article',
  'category',
  'state',
  'issues',
  'textAge',
  'seller',
  'action',
]

/** Ключи localStorage настройки столбцов; общий словарь собирает `lib.ts`. */
export const COLUMNS_LS_KEYS = {
  order: 'seoV2.cards.columns.order',
  visible: 'seoV2.cards.columns.visible',
} as const

const ALL_COLUMN_KEYS = SEO_CARDS_COLUMNS.map((column) => column.key)

/**
 * Нормализация порядка столбцов из произвольного значения. Мусор, дубли
 * и незнакомые ключи отбрасываются, недостающие дописываются в конец
 * в порядке по умолчанию — иначе новый столбец из следующей задачи
 * не появится у открывавших страницу.
 */
export function normalizeColumnsOrder(parsed: unknown): SeoCardsColumnKey[] {
  if (!Array.isArray(parsed)) return [...ALL_COLUMN_KEYS]
  const unique = parsed.filter(
    (key, index): key is SeoCardsColumnKey =>
      typeof key === 'string' &&
      (ALL_COLUMN_KEYS as string[]).includes(key) &&
      parsed.indexOf(key) === index,
  )
  return [...unique, ...ALL_COLUMN_KEYS.filter((key) => !unique.includes(key))]
}

/** Порядок столбцов из хранилища. */
export function readColumnsOrder(): SeoCardsColumnKey[] {
  const raw = window.localStorage.getItem(COLUMNS_LS_KEYS.order)
  if (!raw) return [...ALL_COLUMN_KEYS]
  try {
    return normalizeColumnsOrder(JSON.parse(raw))
  } catch {
    return [...ALL_COLUMN_KEYS]
  }
}

export function writeColumnsOrder(order: SeoCardsColumnKey[]): void {
  window.localStorage.setItem(COLUMNS_LS_KEYS.order, JSON.stringify(order))
}

/**
 * Нормализация видимости столбцов из произвольного значения.
 * Мусор → набор по умолчанию. Заблокированные столбцы всегда видимы.
 */
export function normalizeColumnsVisible(parsed: unknown): Set<SeoCardsColumnKey> {
  const visible = Array.isArray(parsed)
    ? new Set(
        parsed.filter(
          (key): key is SeoCardsColumnKey =>
            typeof key === 'string' && (ALL_COLUMN_KEYS as string[]).includes(key),
        ),
      )
    : new Set(DEFAULT_VISIBLE_COLUMNS)
  for (const column of SEO_CARDS_COLUMNS) {
    if (column.locked) visible.add(column.key)
  }
  return visible
}

/** Видимость столбцов из хранилища. */
export function readColumnsVisible(): Set<SeoCardsColumnKey> {
  const raw = window.localStorage.getItem(COLUMNS_LS_KEYS.visible)
  if (!raw) return normalizeColumnsVisible(null)
  try {
    return normalizeColumnsVisible(JSON.parse(raw))
  } catch {
    return normalizeColumnsVisible(null)
  }
}

export function writeColumnsVisible(visible: Set<SeoCardsColumnKey>): void {
  window.localStorage.setItem(COLUMNS_LS_KEYS.visible, JSON.stringify([...visible]))
}
